import {Injectable, OnDestroy} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest, distinctUntilChanged,
  filter, lastValueFrom,
  map,
  Observable, startWith,
  Subject,
  Subscription,
  switchMap, take, takeUntil,
  tap
} from 'rxjs';
import {DoorType} from '../../models/door';
import {DoorLoader} from './door.loader';
import {FavoriteDoorService} from '../favorite-door/favorite-door.service';
import {FilterService} from '../filter/filter.service';
import {DoorService} from './door.service';
import {PaginationMode} from "../../../generated/graphql";
import {PaginationInformation} from "./graphql.door.loader";
import {DoorCorners, ScenarioType} from "../../models/scenario-image";
import {ProductLoader} from "../product/graphql.product.loader";
import {Product, ProductType} from "../../models/product";
import {CustomerService} from '../customer/customer.service';
import {CustomerServiceImpl} from '../customer/customer.service.impl';

export interface ProductsAtIndex {
  index: number;
  door?: Product
  products?: Array<Product>
}

export type ProductsMap = Map<number, ProductsAtIndex>;

@Injectable({providedIn: 'root'})
export class DoorServiceImpl extends DoorService implements OnDestroy {
  products$ = new BehaviorSubject<Array<Product>>([]);

  // The currently selected door for each door corners index
  selected$ = new BehaviorSubject<ProductsMap>(new Map());

  // The currently selected door corners index
  selectedDoorCornersIndex$ = new BehaviorSubject<number>(1);

  // infinite subscription for door loading mechanisms
  filterSub?: Subscription;
  pageSub?: Subscription;

  private ngUnsubscribe$ = new Subject<void>();

  defaultPaginationInformation = {
    paginationMode: PaginationMode.Page,
    paginationInfo: {
      totalPages: 0,
      page: 1,
      pageSize: 20,
      totalResults: 0
    },
    cursorBasedPaginationInfo: {
      hasNextPage: false,
      startCursor: undefined,
      hasPreviousPage: false,
      endCursor: ''
    }
  }

  currentPaginationInfo: PaginationInformation = this.defaultPaginationInformation
  currentPagination$ = new BehaviorSubject<{ afterCursor?: string, page?: number, mode: PaginationMode }>({
    page: 1,
    afterCursor: undefined,
    mode: PaginationMode.Page
  });

  isLoadingNextPage$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly doorLoader: DoorLoader,
    private readonly productLoader: ProductLoader,
    private readonly customerService: CustomerService,
    private readonly favoriteDoorService: FavoriteDoorService,
    private readonly filterService: FilterService<any>) {
    super();

    // Subscribe only if changed
    this.selectedDoorCornersIndex$
      .pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe$))
      .subscribe(changedIndex => {
        const doorType = this.getSelectedDoorAtIndex(changedIndex)?.doorType
        this.filterService.setFilter({selectedDoorType: doorType})
      })

    /**
     * We want to select the first product of each type by default if no other product is set yet
     * https://github.com/fluegelspiel/door-vision/issues/1007
     */
    this.getSelected$().pipe(takeUntil(this.ngUnsubscribe$)).subscribe(async selected => {

      // Only do this if the customer has enabled products and supported product types are set
      const customerConfig = this.customerService.getCustomerConfig()
      if (!customerConfig || !customerConfig.enabledProducts || !customerConfig.supportedProductTypes || customerConfig.supportedProductTypes.length === 0) {
        return
      }

      const productSelections: Array<{ index: number; products: Array<Product> }> = [];

      // Go over all indexes
      for (const productsAtIndex of selected.values()) {
        const index = productsAtIndex.index;
        const productsToAdd: Array<Product> = [];

        // Go over all possible product types
        for (const productType of customerConfig.supportedProductTypes) {
          if (productType === 'door') continue

          // We now need to check if for the door at this index (and products) at that index a product for that type is already set
          // If this kind of product type has already a selected product we can continue
          const product = (productsAtIndex.products || []).find(p => p.productType === productType)
          if (product) continue

          // Also check if we already added this product type to the list of products to add
          const productToAdd = productsToAdd.find(p => p.productType === productType)
          if (productToAdd) continue

          // If not, we need to select the first product of that type
          const defaultProduct = await this.getDefaultProductForType({ productType, visualDoorId: this.getSelectedDoorAtIndex(index)?.id });

          if (defaultProduct) {
            productsToAdd.push(defaultProduct);
          }
        }

        // If there are new products to add for this index, collect them
        if (productsToAdd.length > 0) {
          productSelections.push({index, products: productsToAdd});
        }
      }

      // Perform selection in one go
      if (productSelections.length > 0) {
        this.selectProductsAtIndex(productSelections);
      }
    })
  }

  async getDefaultProductForType({productType, visualDoorId}: { productType: ProductType, visualDoorId?: string }): Promise<Product | undefined> {
    const products = this.products$.getValue();
    const product = products.find(p => p.productType === productType);

    if (product) {
      return product;
    }

    const loadedProducts = await this.productLoader.getProductsPaginated({
      customerKey: CustomerServiceImpl.GetCustomerKey(),
      productType,
      visualDoorId,//: this.getSelectedDoorAtCurrentIndex()?.id
    }, {
      page: 1,
      pageSize: 1,
      mode: PaginationMode.Page
    }).toPromise()

    return loadedProducts?.result[0]
  }


  public getProductsObservable(): Observable<Array<Product>> {
    return this.products$.asObservable();
  }

  public getProducts$(): Observable<Array<Product>> {
    // start subscription on first access
    if (!this.filterSub) {
      this.pageSub = this.currentPagination$.pipe(switchMap(({page, afterCursor, mode}) => {
        const filter = this.filterService.getFilter();
        const onlyShowFavorites = this.filterService.getOnlyShowFavorites();
        this.isLoadingNextPage$.next(true);

        if ((filter.selectedProductType && filter.selectedProductType === 'door') || filter.selectedProductType === undefined) {
          return this.doorLoader.getDoorsPaginated(CustomerServiceImpl.GetCustomerKey(), {
            ...filter,
            onlyFavorites: onlyShowFavorites
          }, {
            page,
            afterCursor,
            mode
          }).pipe(
            map(({result, ...rest}) => {
              return {result, ...rest}
            })
          )
        }

        return this.productLoader.getProductsPaginated({
          customerKey: CustomerServiceImpl.GetCustomerKey(),
          productType: filter.selectedProductType,
          visualDoorId: this.getSelectedDoorAtCurrentIndex()?.id
        }, {
          page,
          afterCursor,
          mode
        })
      })).subscribe({
        next: ({result, paginationInfo, paginationMode, cursorBasedPaginationInfo}) => {
          this.setProducts([...this.products$.getValue(), ...result]);
          this.currentPaginationInfo = {
            cursorBasedPaginationInfo,
            paginationInfo,
            paginationMode
          }
          this.isLoading$.next(false);
          this.isLoadingNextPage$.next(false);
        },
        error: (error) => {
          console.warn('could not load products', error);
          this.isLoading$.next(false);
          this.isLoadingNextPage$.next(false);
          this.filterSub?.unsubscribe();
          this.filterSub = undefined;
          this.pageSub?.unsubscribe();
          this.pageSub = undefined;

        },
        complete: () => {
          console.error('this subscription should never complete');
        }
      });

      this.filterSub = combineLatest([this.filterService.getFilter$(), this.filterService.getOnlyShowFavorites$()]).pipe(
        tap(([filter]) => {
          this.isLoading$.next(true);
        })
      ).subscribe({
        next: () => {
          // reset page to 1
          this.products$.next([]);
          this.currentPagination$.next({
            page: 1,
            afterCursor: undefined,
            mode: PaginationMode.Page
          });
        },
        error: (error) => {
          console.warn('could not load products', error);
          this.isLoading$.next(false);
          this.filterSub?.unsubscribe();
          this.filterSub = undefined;
          this.pageSub?.unsubscribe();
          this.pageSub = undefined;
        },
        complete: () => {
          console.error('this subscription should never complete');
        }
      });
    }

    return this.products$.asObservable();
  }

  hasNextPage(): boolean {
    if (this.currentPaginationInfo.paginationMode === PaginationMode.Page) {
      const totalPages = this.currentPaginationInfo.paginationInfo?.totalPages ?? 0;
      const currentPage = this.currentPagination$.getValue()?.page ?? 0;
      return totalPages > currentPage;
    }

    return !!(this.currentPaginationInfo.cursorBasedPaginationInfo?.hasNextPage)
  }

  /**
   * Determines if the loading indicator while loading the next page should be shown
   */
  showNextPageLoadingIndicator(): boolean {

    const currentPagination = this.currentPaginationInfo

    if (currentPagination.paginationMode === PaginationMode.Page) {
      return this.hasNextPage() || (this.currentPaginationInfo.paginationInfo?.totalPages === currentPagination.paginationInfo?.totalPages && this.isLoadingNextPage$.getValue())
    }

    return this.hasNextPage() || (this.isLoadingNextPage$.getValue())
  }

  public loadNextPage(): void {
    if (this.hasNextPage() && !this.isLoadingNextPage$.getValue()) {

      const currentPaginationInformation = this.currentPaginationInfo

      if (currentPaginationInformation.paginationMode === PaginationMode.Page) {
        const currentPaginationValues = this.currentPagination$.getValue()
        this.currentPagination$.next({
          page: (currentPaginationValues.page || 0) + 1,
          afterCursor: undefined,
          mode: PaginationMode.Page
        });
        return
      }

      this.currentPagination$.next({
        page: undefined,
        afterCursor: currentPaginationInformation.cursorBasedPaginationInfo?.endCursor,
        mode: PaginationMode.Cursor
      })
    }
  }

  private setProducts(products: Array<Product>): void {
    this.products$.next(products);
  }

  public getSelected$(): Observable<ProductsMap> {
    return this.selected$.asObservable().pipe(
      startWith(new Map()),
      filter(s => !!s),
      distinctUntilChanged((prev, curr) => {
        return DoorServiceImpl.productsMapDebugString(prev) === DoorServiceImpl.productsMapDebugString(curr)
      })) as Observable<ProductsMap>;
  }

  public selectProductAtCurrentIndex(product: Product): void {
    this.selectProductsAtIndex([{index: this.selectedDoorCornersIndex$.getValue(), products: [product]}]);
  }

  public static productsMapDebugString(productsMap: ProductsMap): string {
    let debugString = ''
    for (const [index, productsAtIndex] of productsMap.entries()) {
      debugString += `\n${index}: Door: ${productsAtIndex.door?.name}`
      for (const product of productsAtIndex.products || []) {
        debugString += `\n - ${product.name} (${product.productType})`
      }
    }
    return debugString
  }

  public clearGlazingProduct: Product | undefined;

  private buildNewProductsMapForProducts(products: Array<{ index: number; products: Array<Product> }>): {
    productsMap: ProductsMap;
    changedDoors: Map<number, boolean>;
  } {
    const oldProductsMap = this.selected$.getValue();
    const productsMap = new Map(oldProductsMap); // Clone old state instead of modifying it

    const changedDoors = new Map<number, boolean>();

    products.forEach(({index, products: productArray}) => {
      const previousProducts = productsMap.get(index) || {index, products: []};

      let changedDoor = false;

      // Process new products
      const updatedProducts = productArray.reduce<{ products?: Array<Product>; door?: Product, index: number }>(
        (acc, product) => {
          if (product.productType === "door") {
            changedDoor = acc.door?.id !== product.id;
            acc.door = product;
          } else {
            // Replace product of the same type
            acc.products = (acc.products || []).filter((p) => p.productType !== product.productType);
            acc.products.push(product);
          }
          return acc;
        },
        {...previousProducts} // Clone to avoid modifying the old reference
      );

      // Update map with transformed data
      productsMap.set(index, updatedProducts);
      if (changedDoor) {
        changedDoors.set(index, true);
      }
    });

    return {productsMap, changedDoors};
  }

  public selectProductsAtIndex(products: Array<{ index: number; products: Array<Product> }>): void {
    const {productsMap, changedDoors} = this.buildNewProductsMapForProducts(products)
    this.selected$.next(productsMap);

    if (CustomerServiceImpl.GetCustomerKey().toLowerCase() === 'bghome') {
      for (const product of products) {
        const changedDoor = changedDoors.get(product.index)
        if (!changedDoor) {
          continue
        }

        if (!this.clearGlazingProduct) {
          this.productLoader.getProduct(CustomerServiceImpl.GetCustomerKey(), 'ClearGlass').pipe(take(1)).subscribe(loadedProduct => {
            if (!loadedProduct) {
              return
            }
            this.clearGlazingProduct = loadedProduct
          })
        }

        if (this.clearGlazingProduct) {
          this.selectProductsAtIndex([{products: [this.clearGlazingProduct], index: product.index}])
        }
      }
    }
  }

  public showNextProductAtCurrentIndex(): void {
    const currentSelectedProducts = this.getSelectedProductsAtCurrentIndex();

    if (currentSelectedProducts) {
      const currentProducts = this.products$.getValue();

      if (!currentProducts || currentProducts.length === 0) {
        return
      }
      const currentProductType = currentProducts[0].productType

      const currentlySelectedProductOfCurrentProductType = currentProductType === 'door' ?
        this.getSelectedDoorAtCurrentIndex()
        : currentSelectedProducts.find(p => p.productType === currentProductType)

      if (!currentlySelectedProductOfCurrentProductType) {
        // If no product of the current product type is selected, select the first product of the current product type
        this.selectProductAtCurrentIndex(currentProducts[0]);
        return;
      }

      const productsIndex = currentProducts.findIndex(d => this.arraysContainSameStrings(d.externalIds, currentlySelectedProductOfCurrentProductType.externalIds))

      if (productsIndex === -1 && currentProducts.length > 0) {
        // As a fallback we just show the first product
        this.selectProductAtCurrentIndex(currentProducts[0]);
      } else if (productsIndex < currentProducts.length - 1) {
        this.selectProductAtCurrentIndex(currentProducts[productsIndex + 1]);
      } else if (productsIndex === currentProducts.length - 1) {
        this.selectProductAtCurrentIndex(currentProducts[0]);
      }
    }
  }

  private arraysContainSameStrings(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }

    const sortedArr1 = arr1.slice().sort();
    const sortedArr2 = arr2.slice().sort();

    for (let i = 0; i < sortedArr1.length; i++) {
      if (sortedArr1[i] !== sortedArr2[i]) {
        return false;
      }
    }

    return true;
  }

  public getSelectedProductsAtCurrentIndex(): Array<Product> | undefined {
    return this.getSelectedProductsAtIndex(this.selectedDoorCornersIndex$.getValue());
  }

  public getSelectedDoorAtCurrentIndex(): Product | undefined {
    return this.getSelectedDoorAtIndex(this.selectedDoorCornersIndex$.getValue());
  }

  public getSelectedIndex(): number {
    return this.selectedDoorCornersIndex$.getValue()
  }

  public getSelectedDoorAtIndex(index: number): Product | undefined {
    const productsAtIndex = this.selected$.getValue().get(index);
    return productsAtIndex?.door
  }

  public getSelectedDoorAtCurrentIndex$(): Observable<Product | undefined> {
    return this.selected$.pipe(map(selectedDoors => {
      const productsAtIndex = selectedDoors.get(this.selectedDoorCornersIndex$.getValue());
      return productsAtIndex?.door
    }))
  }

  public getSelectedDoorType$(): Observable<DoorType | undefined> {
    return combineLatest([this.selected$, this.selectedDoorCornersIndex$]).pipe(
      map(([selectedMap, index]) => {
        const productsAtIndex = selectedMap.get(index);

        const doorType = productsAtIndex && productsAtIndex.door ? productsAtIndex.door.doorType : undefined;
        return doorType;
      })
    );
  }

  public getSelectedProductsAtIndex(index: number): Array<Product> | undefined {
    const selectedProducts = this.selected$.getValue();
    return selectedProducts.get(index)?.products;
  }

  public showPrevProductAtCurrentIndex(): void {
    const currentSelectedProducts = this.getSelectedProductsAtCurrentIndex();
    if (currentSelectedProducts) {
      const currentProducts = this.products$.getValue();

      if (!currentProducts || currentProducts.length === 0) {
        return;
      }
      const currentProductType = currentProducts[0].productType

      const currentlySelectedProductOfCurrentProductType = currentProductType === 'door' ?
        this.getSelectedDoorAtCurrentIndex()
        : currentSelectedProducts.find(p => p.productType === currentProductType)

      if (!currentlySelectedProductOfCurrentProductType) {
        // If no product of the current product type is selected, select the first product of the current product type
        this.selectProductAtCurrentIndex(currentProducts[0]);
        return;
      }

      const productsIndex = currentProducts.findIndex(d => this.arraysContainSameStrings(d.externalIds, currentlySelectedProductOfCurrentProductType.externalIds))

      if (productsIndex === -1 && currentProducts.length > 0) {
        // As a fallback we just show the first product
        this.selectProductAtCurrentIndex(currentProducts[0]);
      } else if (productsIndex > 0) {
        this.selectProductAtCurrentIndex(currentProducts[productsIndex - 1]);
      } else if (productsIndex === 0) {
        this.selectProductAtCurrentIndex(currentProducts[currentProducts.length - 1]);
      }
    }
  }

  public getDoorByExternalId(externalId: string): Promise<Product | undefined> {
    return lastValueFrom(this.doorLoader.getDoor(CustomerServiceImpl.GetCustomerKey(), externalId).pipe(take(1)));
  }

  public async setSelectedProductsByExternalIds(products: Array<{
    index: number,
    door?: string,
    products?: Array<string>
  }>): Promise<void> {
    const productSelections: Array<{ index: number; products: Array<Product> }> = [];

    for (const product of products) {
      const productsForIndex: { index: number; products: Array<Product> } = {
        index: product.index,
        products: []
      }

      // Load the full door
      if (product.door) {
        const loadedDoor = await lastValueFrom(this.doorLoader.getDoor(CustomerServiceImpl.GetCustomerKey(), product.door).pipe(take(1)));
        if (loadedDoor) {
          productsForIndex.products.push(loadedDoor)
        }
      }

      for (const productExternalId of product.products || []) {
        // Load the full product
        const loadedProduct = await lastValueFrom(this.productLoader.getProduct(CustomerServiceImpl.GetCustomerKey(), productExternalId).pipe(take(1)))
        if (!loadedProduct) continue
        productsForIndex.products.push(loadedProduct)
      }

      productSelections.push(productsForIndex);
    }
    this.selectProductsAtIndex(productSelections)
  }

  public async toggleFavorite(product: Product): Promise<void> {
    const products = this.products$.getValue()

    const productIndex = products.findIndex(p => p.id === product.id)
    if (productIndex === -1) {
      return
    }

    const productToToggle = products[productIndex]
    productToToggle.isFavorite = await this.favoriteDoorService.toggleFavorite(productToToggle.id, CustomerServiceImpl.GetCustomerKey());

    product.isFavorite = productToToggle.isFavorite

    // Also update the selected door if it is the same as the toggled door
    const selectedDoor = this.getSelectedDoorAtCurrentIndex()
    if (selectedDoor?.id === product.id) {
      this.selectProductAtCurrentIndex(product)
    }
  }

  public getFavoritesCount(): Observable<number> {
    return this.favoriteDoorService.getFavoriteDoorCount(CustomerServiceImpl.GetCustomerKey());
  }

  getSelectedDoorCornersIndex(): Observable<number> {
    return this.selectedDoorCornersIndex$.asObservable();
  }

  public getSelectedDoorCornersIndexValue(): number {
    return this.selectedDoorCornersIndex$.getValue();
  }

  selectDoorCornersIndex(index: number): void {
    this.selectedDoorCornersIndex$.next(index);
  }

  getSelectableIndex$(): Observable<Array<number>> {
    return this.selected$.pipe(map(selectedDoors => {
      const selectableIndex: Array<number> = [];
      for (const [index, doorAtIndex] of selectedDoors.entries()) {
        selectableIndex.push(index);
      }
      return selectableIndex;
    }))
  }

  selectedDoorCorners(doorCorners: DoorCorners, currentScenarioType: ScenarioType | undefined): void {
    this.selectedDoorCornersIndex$.next(doorCorners.index);

    if (currentScenarioType === ScenarioType.EXAMPLE) {
      // This updates the doors panel according to the selected door corners
      this.filterService.setFilter({selectedDoorType: doorCorners.doorType})
    }

  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
